import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Paper,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend
);

const PulseChartDialog = ({ open, onClose, vitals = [] }) => {
  // Build labels
  const labels = vitals.map((v) =>
    v.time ? `${v.date} ${v.time}` : v.date
  );

  // Extract pulse readings as numbers
  const pulse = vitals.map((v) => {
    const p = v.pulse ? parseInt(v.pulse) : null;
    return !isNaN(p) ? p : null;
  });

  // Count valid readings
  const validCount = pulse.filter((p) => p !== null).length;
  const hasInsufficientData = validCount < 2;

  const data = {
    labels,
    datasets: [
      {
        label: "Pulse (bpm)",
        data: pulse,
        borderColor: "#e53935",
        backgroundColor: "rgba(229, 57, 53, 0.25)",
        pointRadius: 6,
        pointHoverRadius: 8,
        pointBackgroundColor: "#e53935",
        pointBorderColor: "#fff",
        borderWidth: 3,
        tension: 0.3,
        spanGaps: true,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        position: "top",
        labels: { font: { size: 14, weight: "bold" } },
      },
      tooltip: {
        backgroundColor: "rgba(0,0,0,0.8)",
        padding: 12,
        callbacks: {
          label: function (ctx) {
            return `Pulse: ${ctx.parsed.y} bpm`;
          },
        },
      },
    },
    scales: {
      y: {
        suggestedMin: 40,
        suggestedMax: 140,
        title: {
          display: true,
          text: "Pulse (bpm)",
          font: { size: 14, weight: "bold" },
        },
        ticks: {
          callback: (value) => `${value} bpm`,
        },
      },
      x: {
        title: {
          display: true,
          text: "Date & Time",
          font: { size: 14, weight: "bold" },
        },
        ticks: { maxRotation: 45, minRotation: 45 },
      },
    },
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle
        sx={{
          background: "linear-gradient(135deg, #19b934ff 0%, #19b934ff 100%)",
          color: "white",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontWeight: 700,
        }}
      >
        Pulse Chart
        <IconButton onClick={onClose} sx={{ color: "white" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3, bgcolor: 'background.default' }}>
        {/* No data */}
        {vitals.length === 0 && (
          <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
            <Typography fontWeight={600}>No pulse data available</Typography>
            <Typography variant="caption">
              Pulse readings will appear here once recorded.
            </Typography>
          </Alert>
        )}

        {/* Not enough data */}
        {vitals.length > 0 && hasInsufficientData && (
          <Alert severity="warning" sx={{ mb: 3, borderRadius: 2 }}>
            <Typography fontWeight={600}>Not enough pulse data</Typography>
            <Typography variant="caption">
              At least 2 pulse readings are required. Current: {validCount}
            </Typography>
          </Alert>
        )}

        {/* Chart + Observations */}
        {vitals.length > 0 && (
          <>
            <Paper
              sx={{
                p: 3,
                borderRadius: 3,
                boxShadow: "0 2px 10px rgba(0,0,0,0.15)",
                mb: 3,
              }}
            >
              <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
                Pulse Trend
              </Typography>

              <Box sx={{ minHeight: 300 }}>
                <Line data={data} options={options} />
              </Box>
            </Paper>

            <Paper sx={{ p: 2, borderRadius: 2, bgcolor: "grey.100" }}>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                Observations
              </Typography>

              {vitals.map((v, i) => (
                <Typography key={i} sx={{ fontSize: "0.9rem", mb: 0.5 }}>
                  • <b>{v.pulse} bpm</b> on <b>{v.date}</b>{" "}
                  {v.time && <>at <b>{v.time}</b></>}
                </Typography>
              ))}
            </Paper>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button
          onClick={onClose}
          variant="contained"
          color="success"
          sx={{ textTransform: "none", fontWeight: 600 }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PulseChartDialog;
